"use client"

import { ArrayPath, Controller, FieldPath, FieldValues, useFieldArray, useFormContext } from "react-hook-form"
import { TButton } from "../../ui/button/TButton"
import { TIconButton } from "../../ui/button/TIconButton"
import { TIcon } from "../../ui/display/TIcon"
import { TNumberInput } from "../../ui/input/TNumberInput"
import { TTextInput } from "../../ui/input/TTextInput"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { DynamicFormLabel } from "../components/form-label"
import type { ParsedField } from "../types"

interface ArrayFieldProps {
	field: ParsedField
	/** 항목 추가 버튼 라벨 — 소비 앱에서 i18n 번역값을 주입한다 (미지정 시 기존 "Add item") */
	addItemLabel?: string
}

export function ArrayField({ field: parsedField, addItemLabel = "Add item" }: ArrayFieldProps) {
	const { name, schema, required } = parsedField
	const description = schema.description
	const title = schema.title
	const { control } = useFormContext()
	const { fields, append, remove } = useFieldArray({ control, name: name as ArrayPath<FieldValues> })
	const itemSchema = Array.isArray(schema.items) ? schema.items[0] : schema.items
	const itemType = itemSchema?.type || "string"

	function getPlaceholder(index: number): string {
		if (itemType === "integer" || itemType === "number") {
			return `값 ${index + 1}`
		}
		return `항목 ${index + 1}`
	}

	function handleAddItem() {
		if (itemType === "integer" || itemType === "number") {
			append(0 as never)
		} else {
			append("" as never)
		}
	}

	return (
		<FormItem className="bg-g800/10 rounded-lg p-3 flex flex-col group">
			<DynamicFormLabel name={name} title={title} required={required} description={description} />

			{fields.map((field, index) => (
				<div key={field.id} className="flex items-center gap-2">
					<Controller
						name={`${name}.${index}` as FieldPath<FieldValues>}
						control={control}
						render={({ field: inputField }) => (
							<FormControl>
								{itemType === "integer" || itemType === "number" ? (
									<TNumberInput
										size="small"
										value={typeof inputField.value === "number" ? inputField.value : undefined}
										placeholder={getPlaceholder(index)}
										onChange={(value: number) => {
											inputField.onChange(value)
										}}
									/>
								) : (
									<TTextInput
										{...inputField}
										size="small"
										value={inputField.value ?? ""}
										placeholder={getPlaceholder(index)}
										onChange={(value: string) => {
											inputField.onChange(value)
										}}
									/>
								)}
							</FormControl>
						)}
					/>
					<TIconButton name="bin" size="small" color="tertiary" onClick={() => remove(index)} />
				</div>
			))}

			<TButton color="blurry" size="small" onClick={handleAddItem}>
				<TIcon name="add" size={16} />
				{addItemLabel}
			</TButton>

			<FormMessage />
		</FormItem>
	)
}
